<template>
  <div 
    class="fader-widget has-background-white p-3 is-horizontal" 
  >
  
    <!-- Speaker Icon -->
    <div class="icon-container">
      <span 
        class="icon is-clickable" 
        :class="isMuted ? 'has-text-danger' : 'has-text-link'"
        @click="toggleMute"
      >
        <font-awesome-icon :icon="isMuted ? 'fas fa-volume-mute' : 'fas fa-volume-up'" />
      </span>
    </div>

    <!-- The Fader -->
    <div class="fader-track-wrapper">
      <input 
        type="range" 
        class="volume-fader" 
        v-model.number="volume"
        min="0" 
        max="100"
        @input="update"
        :disabled="isMuted"
      >
    </div>

    <!-- Label and dB Readout -->
    <div class="info-container mt-2 is-flex is-align-items-center">
      <span class="has-text-weight-bold is-size-7 has-text-grey-dark is-uppercase">
        {{ label }}
      </span>
      <!-- Monospaced dB readout for a technical look -->
      <span class="ml-auto is-family-monospace is-size-7 has-text-grey">
        {{ volumeDb }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useFaderStore } from '@/stores/faderStore';

const props = defineProps({
  id: { type: String, required: true }, // Unique key for this instance
  label: { type: String, default: 'Volume' },
});

const emit = defineEmits(['update']);

const store = useFaderStore();
const faderState = store.getFader(props.id);

// Sync models to the specific ID in the store
const volume = defineModel('volume', { 
  get: () => faderState.volume,
  set: (val) => { faderState.volume = val; }
});

const isMuted = defineModel('isMuted', { 
  get: () => faderState.isMuted,
  set: (val) => { faderState.isMuted = val; }
});

// Map 0-100% to -60dB to 0dB
const volumeDb = computed(() => {
  if (volume.value === 0) return "-∞ dB";
  const minDb = -60;
  const maxDb = 0;
  const db = minDb + (volume.value / 100) * (maxDb - minDb);
  return `${db.toFixed(1)} dB`;
});


const update = () => {
  emit('update', {
    muted: isMuted.value,
    level: volume.value / 100
  });  
}

const toggleMute = () => {
  isMuted.value = !isMuted.value;
  update()
};


</script>

<style scoped>
.fader-widget {
  display: inline-flex;
  flex-direction: column;
  /* border: 1px solid #dbdbdb;
  border-bottom: 2px solid #dbdbdb; */
}

/* Horizontal Layout */
.is-horizontal {
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}
.is-horizontal .icon-container { margin-right: 1rem; }
.is-horizontal .fader-track-wrapper { flex-grow: 1; }
.is-horizontal .info-container { width: 100%; border-top: 1px solid #f5f5f5; padding-top: 4px; }


/* Flat Fader Styling */
.volume-fader {
  -webkit-appearance: none;
  background: transparent;
  cursor: pointer;
}

.is-horizontal .volume-fader { width: 100%; height: 20px; }

/* Track */
.volume-fader::-webkit-slider-runnable-track {
  background-color: #f5f5f5;
  border: 1px solid #dbdbdb;
  border-radius: 0;
}
.is-horizontal .volume-fader::-webkit-slider-runnable-track {
  height: 6px;
  background-image: repeating-linear-gradient(90deg, #dbdbdb 0, #dbdbdb 1px, transparent 1px, transparent 10%);
}

/* Thumb/Handle */
.volume-fader::-webkit-slider-thumb {
  -webkit-appearance: none;
  background: #4a4a4a;
  border-radius: 0;
  border: none;
}
.is-horizontal .volume-fader::-webkit-slider-thumb { height: 18px; width: 10px; margin-top: -7px; }

.volume-fader:not(:disabled)::-webkit-slider-thumb:hover { background: #3273dc; }
.volume-fader:disabled { opacity: 0.4; }
</style>
